import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { ChevronLeft, ChevronRight, Zap, Target, BookOpen, CheckCircle, Circle, ChevronDown, ChevronUp } from 'lucide-react';
import PhaseNavigator from '../components/PhaseNavigator';
import TaskItem from '../components/TaskItem';
import PlaybookCompletionDialog from '../components/PlaybookCompletionDialog';
import { useCourses, CourseWithProgress } from '@/hooks/useCourses';
import { Spinner } from '@/components/ui/loading-spinner';
import { usePlaybookProgress } from '@/hooks/usePlaybookProgress';
import { Task, Phase, Playbook } from '@/features/courses/types';

const CourseActivePage = () => {
  const { id: projectId, playbookId } = useParams<{ id: string; playbookId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const { useGetCourseDetails, updateTaskProgress: updateTaskMutation } = useCourses();
  const { data: courseWithProgress, isLoading, error: courseError } = useGetCourseDetails(playbookId || '');
  
  const [activePhaseId, setActivePhaseId] = useState<string>('');
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [showProTip, setShowProTip] = useState(false);
  const [isKeyTargetsExpanded, setIsKeyTargetsExpanded] = useState(false);
  
  const {
    completedTasks,
    toggleTask,
    calculatePhaseProgress,
    setActivePhase
  } = usePlaybookProgress(projectId);
  
  useEffect(() => {
    if (isLoading || !courseWithProgress) return;

    console.log("CourseActivePage - Initial Load");
    console.log("Course with progress:", courseWithProgress);
    console.log("User progress:", courseWithProgress.userProgress);
    console.log("Completed task IDs in CourseActivePage:", completedTasks);

    // Get the phase ID from URL query parameter if present
    const searchParams = new URLSearchParams(location.search);
    const phaseFromUrl = searchParams.get('phase');
    console.log("Phase ID from URL:", phaseFromUrl);

    let initialPhaseId = '';
    
    // Priority 1: Use phase ID from URL if it exists and is valid
    if (phaseFromUrl && courseWithProgress.phases.some(p => p.id === phaseFromUrl)) {
      initialPhaseId = phaseFromUrl;
      console.log("Using phase ID from URL:", initialPhaseId);
      
      // Find phase details for logging
      const phaseDetails = courseWithProgress.phases.find(p => p.id === phaseFromUrl);
      if (phaseDetails) {
        console.log("Selected phase from URL:", {
          title: phaseDetails.title,
          index: courseWithProgress.phases.findIndex(p => p.id === phaseFromUrl) + 1
        });
      }
    }
    // Priority 2: Check if there's a current phase in userProgress
    else if (courseWithProgress.userProgress?.current_phase_id) {
      const currentPhaseId = courseWithProgress.userProgress.current_phase_id;
      console.log("Current phase ID from userProgress:", currentPhaseId);
      
      // Verify the phase exists in the course phases
      const phaseExists = courseWithProgress.phases.some(p => p.id === currentPhaseId);
      console.log("Phase exists in course:", phaseExists);
      
      if (phaseExists) {
        initialPhaseId = currentPhaseId;
        console.log("Using current phase ID from userProgress:", initialPhaseId);
        
        // Find phase details for logging
        const phaseDetails = courseWithProgress.phases.find(p => p.id === currentPhaseId);
        if (phaseDetails) {
          console.log("Selected phase from userProgress:", {
            title: phaseDetails.title,
            index: courseWithProgress.phases.findIndex(p => p.id === currentPhaseId) + 1
          });
        }
      }
    }

    // Priority 3: Fall back to first phase if no other valid phase ID
    if (!initialPhaseId && courseWithProgress.phases?.length > 0) {
      initialPhaseId = courseWithProgress.phases[0].id;
      console.log("No valid current phase, using first phase:", initialPhaseId);
      
      // Find phase details for logging
      const phaseDetails = courseWithProgress.phases[0];
      if (phaseDetails) {
        console.log("Selected first phase as fallback:", {
          title: phaseDetails.title,
          index: 1
        });
      }
    }
    
    console.log("Setting active phase ID to:", initialPhaseId);
    if (initialPhaseId) {
        setActivePhaseId(initialPhaseId);
    }
  }, [courseWithProgress, isLoading, location.search]);

  useEffect(() => {
    if (playbookId && activePhaseId) {
      console.log(`Setting active phase in database: playbookId=${playbookId}, activePhaseId=${activePhaseId}`);
      
      // Find phase details for logging
      if (courseWithProgress && !isLoading) {
        const phaseDetails = courseWithProgress.phases.find(p => p.id === activePhaseId);
        if (phaseDetails) {
          console.log("Activating phase in database:", {
            title: phaseDetails.title,
            index: courseWithProgress.phases.findIndex(p => p.id === activePhaseId) + 1
          });
        }
      }
      
      setActivePhase(playbookId, activePhaseId)
        .then(() => {
          console.log("Successfully set active phase in database");
        })
        .catch(error => {
          console.error("Failed to set active phase:", error);
        });
    }
  }, [playbookId, activePhaseId, setActivePhase, courseWithProgress, isLoading]);

  const handleToggleTaskComplete = useCallback((taskId: string, isCompleted: boolean) => {
    if (!activePhaseId || !playbookId) return;
    
    toggleTask(taskId, activePhaseId, isCompleted).catch(error => {
      console.error('Failed to toggle task completion:', error);
    });
    
    // Also update via the mutation for backward compatibility
    updateTaskMutation.mutate({
      courseId: playbookId,
      phaseId: activePhaseId,
      taskId,
      isCompleted
    });
  }, [activePhaseId, playbookId, toggleTask, updateTaskMutation]);

  if (isLoading) {
    return (
      <div className="container py-4 sm:py-6 max-w-7xl mx-auto bg-white dark:bg-zinc-900 min-h-screen px-4 sm:px-6">
        <div className="flex justify-center py-12"><Spinner className="h-8 w-8 text-blue-500 dark:text-blue-400" /></div>
      </div>
    );
  }

  if (courseError || !courseWithProgress) {
    return (
      <div className="container py-4 sm:py-6 max-w-7xl mx-auto bg-white dark:bg-zinc-900 min-h-screen px-4 sm:px-6">
        <div className="text-center py-12">
          <h2 className="text-xl sm:text-2xl font-semibold text-zinc-900 dark:text-zinc-100">Course not found</h2>
          <p className="mt-2 text-zinc-600 dark:text-zinc-400 px-4">
            {courseError ? courseError.message : "The course you're looking for doesn't exist or has been removed."}
          </p>
          <Button className="mt-4" onClick={() => navigate(`/projects/${projectId}/playbooks`)}>
            Back to Courses
          </Button>
        </div>
      </div>
    );
  }

  const activePhase = courseWithProgress.phases.find(phase => phase.id === activePhaseId);

  if (!activePhase) {
    return (
        <div className="container py-4 sm:py-6 max-w-7xl mx-auto bg-white dark:bg-zinc-900 min-h-screen px-4 sm:px-6">
            <div className="text-center py-12">
                <h2 className="text-xl sm:text-2xl font-semibold text-zinc-900 dark:text-zinc-100">Phase not available</h2>
                <p className="mt-2 text-zinc-600 dark:text-zinc-400 px-4">The selected phase is not available or the course has no phases.</p>
                <Button 
                    className="mt-4"
                    onClick={() => {
                        if (courseWithProgress.phases && courseWithProgress.phases.length > 0) {
                            setActivePhaseId(courseWithProgress.phases[0].id);
                        } else {
                            navigate(`/projects/${projectId}/playbooks/${playbookId}`);
                        }
                    }}
                >
                    {courseWithProgress.phases && courseWithProgress.phases.length > 0 ? 'Go to First Phase' : 'Back to Course Details'}
                </Button>
            </div>
        </div>
    );
  }
  
  const currentPhaseIndex = courseWithProgress.phases.findIndex(phase => phase.id === activePhaseId);
  const isFirstPhase = currentPhaseIndex === 0;
  const isLastPhase = currentPhaseIndex === courseWithProgress.phases.length - 1;

  const handlePreviousPhase = () => {
    if (!isFirstPhase) {
      const newPhaseId = courseWithProgress.phases[currentPhaseIndex - 1].id;
      setActivePhaseId(newPhaseId);
      
      // Update URL with the new phase ID
      navigate(`/projects/${projectId}/playbooks/${playbookId}/active?phase=${newPhaseId}`, {
        replace: true
      });
    }
  };

  const handleNextPhase = () => {
    if (!isLastPhase) {
      const newPhaseId = courseWithProgress.phases[currentPhaseIndex + 1].id;
      setActivePhaseId(newPhaseId);
      
      // Update URL with the new phase ID
      navigate(`/projects/${projectId}/playbooks/${playbookId}/active?phase=${newPhaseId}`, {
        replace: true
      });
    } else {
      setShowCompletionDialog(true);
    }
  };

  const handleCloseCompletionDialog = () => setShowCompletionDialog(false);
  const handleViewPlaybook = () => {
    setShowCompletionDialog(false);
    navigate(`/projects/${projectId}/playbooks/${playbookId}`);
  };
  const toggleProTip = () => setShowProTip(!showProTip);

  // Calculate total tasks excluding Pro Tips
  const totalTasksInCourse = courseWithProgress.phases.reduce((sum, phase) => {
    const nonProTipTasks = phase.tasks?.filter(task => task.title !== 'Pro Tip') || [];
    return sum + nonProTipTasks.length;
  }, 0);
  
  // Get only non-Pro-Tip task IDs for this playbook to be consistent
  const currentPlaybookTaskIds = courseWithProgress.phases.flatMap(phase => 
    phase.tasks?.filter(task => task.title !== 'Pro Tip').map(task => task.id) || []
  );
  
  // Count completed tasks that belong only to this playbook (excluding Pro Tips)
  const completedTasksCount = Object.keys(completedTasks)
    .filter(id => completedTasks[id] && currentPlaybookTaskIds.includes(id))
    .length;
    
  const completionPercent = totalTasksInCourse > 0
    ? Math.round((completedTasksCount / totalTasksInCourse) * 100)
    : 0;

  const phaseProgress = activePhaseId ? calculatePhaseProgress(activePhaseId) : { percentage: 0, completedCount: 0, totalTasks: 0 };
    
  const playbookForDialog: Playbook = {
    id: courseWithProgress.id,
    title: courseWithProgress.title,
    cardImage: courseWithProgress.card_image_url || '',
    detailImage: courseWithProgress.detail_image_url || '',
    card_image_url: courseWithProgress.card_image_url,
    detail_image_url: courseWithProgress.detail_image_url,
    shortDescription: courseWithProgress.shortDescription,
    outcomes: courseWithProgress.outcomes || [],
    newSkills: courseWithProgress.newSkills || [],
    enrolledUserAvatars: [],
    totalEnrolledUsers: 0,
    introduction: courseWithProgress.introduction || '',
    keyTargets: courseWithProgress.keyTargets?.map(target => 
      typeof target === 'string' 
        ? { title: target, description: '' } 
        : target
    ) || [],
    phases: courseWithProgress.phases || [],
    overallProgress: completionPercent,
    totalTasks: totalTasksInCourse,
    completedTasks: completedTasksCount
  };

  const filteredTasks = activePhase.tasks?.filter(task => task.title !== 'Pro Tip') || [];
  const hasProTip = activePhase.tasks?.some(task => task.title === 'Pro Tip');
  const proTipTask = activePhase.tasks?.find(task => task.title === 'Pro Tip');
  const proTipContent = proTipTask?.hint || '';

  // Prepare tasks with completion status from completedTasks
  const tasksWithStatus = filteredTasks.map(task => ({
    ...task,
    isCompleted: completedTasks[task.id] === true
  }));

  return (
    <div className="w-full bg-white dark:bg-zinc-900 min-h-screen">
      {/* Mobile-first header with better spacing and layout */}
      <div className="w-full bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-700">
        <div className="container max-w-[1200px] mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex flex-col space-y-6 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 sm:gap-6">
            <div className="flex items-start gap-4 sm:gap-6">
              <div className="bg-gradient-to-br from-blue-600 to-sky-500 h-14 w-14 sm:h-16 sm:w-16 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                <Zap className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="font-bold text-xl sm:text-2xl lg:text-3xl bg-gradient-to-r gradient-text text-transparent leading-tight">
                  {courseWithProgress.title}
                </h1>
                <p className="text-zinc-600 dark:text-zinc-400 mt-2 font-medium text-sm sm:text-base lg:text-lg">
                  Your step-by-step guide to sustainable SaaS growth
                </p>
              </div>
            </div>
            
            {/* Minimal progress indicator - hidden on mobile, subtle on desktop */}
            <div className="hidden sm:block">
              <div className="text-right">
                <div className="flex items-center justify-end gap-2 mb-1">
                  <span className="text-xs text-zinc-500 dark:text-zinc-400">Progress</span>
                  <span className={`text-sm font-medium ${completionPercent === 100 ? 'text-green-600 dark:text-green-400' : 'text-primary dark:text-blue-400'}`}>
                    {completionPercent}%
                  </span>
                </div>
                <div className="w-20 bg-zinc-200 dark:bg-zinc-700 rounded-full h-1.5">
                  <div 
                    className={`h-1.5 rounded-full transition-all duration-700 ${completionPercent === 100 ? "bg-gradient-to-r from-green-500 to-emerald-500" : "bg-gradient-to-r from-blue-600 to-sky-500"}`} 
                    style={{ width: `${completionPercent}%` }}
                  ></div>
                </div>
                <div className="text-xs text-zinc-400 dark:text-zinc-500 mt-1">
                  {completedTasksCount}/{totalTasksInCourse} tasks
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="container max-w-[1200px] mx-auto px-4 sm:px-6">
        {/* Mobile-optimized Phase Navigator with better spacing */}
        <div className="py-6 sm:py-8">
          <PhaseNavigator
            phases={courseWithProgress.phases.map(p => {
              // Get only non-Pro-Tip task IDs for this phase to be consistent
              const phaseTaskIds = p.tasks?.filter(task => task.title !== 'Pro Tip').map(task => task.id) || [];
              const phaseCompletedCount = phaseTaskIds.filter(id => completedTasks[id]).length;
              const phaseTotalTasks = p.tasks?.filter(task => task.title !== 'Pro Tip').length || 0;
              const phasePercentage = phaseTotalTasks > 0 
                ? Math.round((phaseCompletedCount / phaseTotalTasks) * 100) 
                : 0;
              
              return {
              ...p,
                progress: phasePercentage
              };
            })}
            activePhaseId={activePhaseId}
            onSelectPhase={setActivePhaseId}
          />
        </div>
        
        {/* Mobile-optimized main content with proper spacing */}
        <div className="pb-8 sm:pb-16 space-y-8 sm:space-y-8">
          {/* Enhanced unified phase content card */}
          <div className="bg-white dark:bg-zinc-800 rounded-3xl border border-zinc-200 dark:border-zinc-700 shadow-lg overflow-hidden">
            {/* Phase header with better mobile layout */}
            <div className="bg-gradient-to-br from-zinc-50 to-white dark:from-zinc-800 dark:to-zinc-800 p-6 sm:p-8 border-b border-zinc-200 dark:border-zinc-700">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
                <div className="lg:col-span-2">
                  <div className="flex items-start gap-4 sm:gap-4 mb-6">
                    <div className="bg-gradient-to-br from-blue-50 to-sky-50 dark:from-blue-950/20 dark:to-sky-950/30 rounded-2xl p-3">
                      <BookOpen className="h-6 w-6 sm:h-6 sm:w-6 text-primary dark:text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-zinc-900 dark:text-zinc-100 mb-3 leading-tight">
                        Phase {currentPhaseIndex + 1}: {activePhase.title?.replace(/^Phase \d+ -\s*/i, '')}
                      </h2>
                    </div>
                  </div>
                  <div className="prose prose-slate dark:prose-invert max-w-none">
                    <div className="text-zinc-700 dark:text-zinc-300 space-y-4 leading-relaxed text-base">
                      {(activePhase.description || '').split('\n').map((paragraph, idx) => (
                        <p key={idx} className="mb-0">{paragraph}</p>
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* Mobile-optimized key targets sidebar */}
                <div className="lg:col-span-1">
                  <div className="bg-white/50 dark:bg-zinc-900/50 rounded-2xl p-6 border border-zinc-200/50 dark:border-zinc-700/50">
                    {/* Mobile: Collapsible key targets with better touch targets */}
                    <div 
                      className="flex items-center justify-between mb-6 cursor-pointer lg:cursor-default"
                      onClick={() => setIsKeyTargetsExpanded(!isKeyTargetsExpanded)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="bg-gradient-to-br from-blue-50 to-sky-50 dark:from-blue-950/20 dark:to-sky-950/30 rounded-xl p-2">
                          <Target className="h-5 w-5 text-primary dark:text-blue-400" />
                        </div>
                        <h3 className="font-bold text-zinc-900 dark:text-zinc-100 text-base sm:text-lg">Key Targets</h3>
                      </div>
                      {/* Mobile expand/collapse icon with better visual feedback */}
                      <div className="lg:hidden bg-zinc-100 dark:bg-zinc-700 rounded-lg p-2">
                        {isKeyTargetsExpanded ? (
                          <ChevronUp className="h-5 w-5 text-zinc-500" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-zinc-500" />
                        )}
                      </div>
                    </div>
                    
                    {/* Key targets content - collapsible on mobile with better spacing */}
                    <div className={`lg:block ${isKeyTargetsExpanded ? 'block' : 'hidden'}`}>
                      <ul className="space-y-4">
                        {activePhase.keyTargets?.map((target, idx) => {
                          // Handle both string and object target formats
                          let title, description;
                          if (typeof target === 'string') {
                            // If it's a string, parse it to extract title and description
                            const colonIndex = target.indexOf(':');
                            if (colonIndex > 0) {
                              title = target.substring(0, colonIndex).trim();
                              description = target.substring(colonIndex + 1).trim();
                            } else {
                              title = target;
                              description = '';
                            }
                          } else {
                            // If it's already an object, use the properties
                            title = target.title;
                            description = target.description;
                          }
                          
                          return (
                            <li key={idx} className="flex items-start gap-3">
                              <div className="text-primary dark:text-blue-400 mt-1 h-4 w-4 flex items-center justify-center flex-shrink-0">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                  <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="flex-1">
                                <div className="font-semibold text-zinc-900 dark:text-zinc-100 text-sm mb-1">
                                  {title}
                                </div>
                                {description && (
                                  <div className="text-zinc-600 dark:text-zinc-400 text-sm leading-relaxed">
                                    {description}
                                  </div>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                      
                      {hasProTip && (
                        <button 
                          className="mt-6 w-full flex items-center justify-center gap-2 text-sm font-medium text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 bg-amber-50 dark:bg-amber-950/30 hover:bg-amber-100 dark:hover:bg-amber-950/50 rounded-xl py-3 px-4 transition-all duration-200 border border-amber-200/50 dark:border-amber-800/30"
                          onClick={toggleProTip}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                            <path d="M10 1a6 6 0 00-6 6v1.5a.5.5 0 01-.5.5H3a1 1 0 00-1 1v1a1 1 0 001 1h.5a.5.5 0 01.5.5V14a1 1 0 001 1h3.5a1 1 0 001-1v-2.5a.5.5 0 01.5-.5H10a.5.5 0 01.5.5V14a1 1 0 001 1h3.5a1 1 0 001-1v-1.5a.5.5 0 01.5-.5H17a1 1 0 001-1v-1a1 1 0 00-1-1h-.5a.5.5 0 01-.5-.5V7a6 6 0 00-6-6z" />
                          </svg>
                          {showProTip ? 'Hide Pro Tip' : 'Show Pro Tip'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Pro tip section - mobile optimized with better design */}
            {showProTip && hasProTip && (
              <div className="bg-gradient-to-br from-amber-50 to-orange-50/30 dark:from-amber-950/20 dark:to-orange-950/10 border-b border-amber-200/50 dark:border-amber-800/30">
                <div className="p-6 sm:p-6">
                  <div className="flex gap-4">
                    <div className="h-10 w-10 sm:h-10 sm:w-10 flex items-center justify-center bg-gradient-to-br from-amber-100 to-yellow-100 dark:from-amber-900/50 dark:to-yellow-900/50 rounded-xl text-amber-600 dark:text-amber-400 flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-amber-900 dark:text-amber-200 mb-3 text-base sm:text-lg">💡 Pro Tip</h4>
                      <div className="text-amber-800 dark:text-amber-300 space-y-3 leading-relaxed text-sm sm:text-base">
                        {proTipContent.split('\n').map((line, idx) => <p key={idx} className="mb-0">{line}</p>)}
                      </div>
                    </div>
                    <button 
                      onClick={toggleProTip} 
                      className="text-amber-500 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 h-8 w-8 flex items-center justify-center rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-all duration-200 flex-shrink-0" 
                      aria-label="Close pro tip"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Tasks section header - mobile optimized */}
            <div className="bg-zinc-50 dark:bg-zinc-800/50 p-6 sm:p-6 border-b border-zinc-200 dark:border-zinc-700">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-sky-50 dark:from-blue-950/20 dark:to-sky-950/30 rounded-xl p-2.5">
                    <CheckCircle className="h-5 w-5 text-primary dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-base sm:text-lg text-zinc-900 dark:text-zinc-100">Complete These Tasks</h3>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400">
                      {phaseProgress.completedCount} of {phaseProgress.totalTasks} tasks completed
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <div className="w-32 sm:w-32 bg-zinc-200 dark:bg-zinc-700 rounded-full h-2.5">
                    <div 
                      className="h-2.5 rounded-full bg-gradient-to-r from-blue-600 to-sky-500 transition-all duration-500"
                      style={{ width: `${phaseProgress.percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-primary dark:text-blue-400 min-w-[45px]">
                    {phaseProgress.percentage}%
                  </span>
                </div>
              </div>
            </div>
            
            {/* Tasks list */}
            <div className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {tasksWithStatus.map((task, index) => (
                <TaskItem
                  key={task.id}
                  task={task}
                  index={index + 1}
                  onToggleComplete={handleToggleTaskComplete}
                />
              ))}
            </div>
          </div>
          
          {/* Mobile-optimized navigation buttons with better touch targets */}
          <div className="flex flex-col sm:flex-row justify-between items-center pt-6 sm:pt-8 gap-4 sm:gap-0">
            <Button
              variant="outline"
              className="w-full sm:w-auto px-8 py-4 h-14 sm:h-12 border-zinc-300 dark:border-zinc-600 rounded-xl text-zinc-700 dark:text-zinc-300 font-medium hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-all duration-200 bg-white dark:bg-zinc-800 shadow-sm disabled:opacity-50 order-2 sm:order-1 text-base sm:text-sm"
              onClick={handlePreviousPhase}
              disabled={isFirstPhase}
            >
              <ChevronLeft className="h-5 w-5 mr-2" />
              Previous Phase
            </Button>
            
            <Button
              className="w-full sm:w-auto px-8 py-4 h-14 sm:h-12 bg-gradient-to-r from-blue-600 to-sky-500 hover:opacity-90 transition-all duration-200 rounded-xl text-white font-medium shadow-lg hover:shadow-xl hover:-translate-y-0.5 order-1 sm:order-2 text-base sm:text-sm"
              onClick={handleNextPhase}
            >
              {isLastPhase ? 'Complete Course' : 'Next Phase'}
              <ChevronRight className="h-5 w-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>
      
      {showCompletionDialog && (
        <PlaybookCompletionDialog
          playbook={playbookForDialog}
          isOpen={showCompletionDialog}
          onClose={handleCloseCompletionDialog}
          onViewPlaybook={handleViewPlaybook}
        />
      )}
    </div>
  );
};

export default CourseActivePage; 